import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import gsap from "gsap";
import { useSceneStore } from "@/state/useSceneStore";
import { useProgressStore } from "@/state/useProgressStore";

let hasRecordedVisit = false;
const AUTO_HIDE_MS = 4000;

export const Plant = (): JSX.Element | null => {
  const mesh = useSceneStore((s) => s.objects.plant);
  const stage = useProgressStore((s) => s.plantStage);
  const visitCount = useProgressStore((s) => s.visitCount);
  const totalActiveSeconds = useProgressStore((s) => s.totalActiveSeconds);
  const recordVisit = useProgressStore((s) => s.recordVisit);
  const addActiveSecond = useProgressStore((s) => s.addActiveSecond);
  const blurbOpen = useSceneStore((s) => s.plantBlurbOpen);
  const togglePlantBlurb = useSceneStore((s) => s.togglePlantBlurb);
  const autoHide = useRef<number | null>(null);

  useEffect(() => {
    if (hasRecordedVisit) return;
    hasRecordedVisit = true;
    recordVisit();
  }, [recordVisit]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) addActiveSecond();
    }, 1000);
    return () => window.clearInterval(id);
  }, [addActiveSecond]);

  useEffect(() => {
    if (!mesh) return;
    const targetScale = 0.6 + stage * 0.12;
    const tween = gsap.to(mesh.scale, {
      x: targetScale,
      y: targetScale,
      z: targetScale,
      duration: 1.2,
    });
    return () => {
      tween.kill();
    };
  }, [mesh, stage]);

  useEffect(() => {
    if (!blurbOpen) {
      if (autoHide.current !== null) {
        window.clearTimeout(autoHide.current);
        autoHide.current = null;
      }
      return;
    }
    autoHide.current = window.setTimeout(() => {
      togglePlantBlurb();
      autoHide.current = null;
    }, AUTO_HIDE_MS);
    return () => {
      if (autoHide.current !== null) {
        window.clearTimeout(autoHide.current);
        autoHide.current = null;
      }
    };
  }, [blurbOpen, togglePlantBlurb]);

  if (!mesh || !blurbOpen) return null;

  const minutes = Math.floor(totalActiveSeconds / 60);
  const remSeconds = totalActiveSeconds % 60;
  const timeStr =
    minutes > 0 ? `${minutes}m ${remSeconds}s` : `${remSeconds}s`;

  const worldPos = new THREE.Vector3();
  mesh.getWorldPosition(worldPos);
  worldPos.y += 0.6;

  return (
    <Html
      position={[worldPos.x, worldPos.y, worldPos.z]}
      zIndexRange={[10, 0]}
    >
      <div className="pointer-events-none -translate-x-1/2 -translate-y-full rounded-sm border border-phosphor bg-bg1/90 px-3 py-2 font-mono text-xs text-phosphor backdrop-blur-sm">
        <div className="uppercase tracking-wider text-ink2">
          plant · stage {stage}/4
        </div>
        <div>
          {visitCount} {visitCount === 1 ? "visit" : "visits"} · {timeStr} here
        </div>
      </div>
    </Html>
  );
};
